import { inject, Injectable } from '@angular/core'
import { HttpContext } from '@angular/common/http'

import {
  VehicleRequestQueryParams,
  PlateToVinResponse,
  VehicleMakes,
  VehicleModels,
  VehicleYears,
  VehicleDetailsResponse
} from './vehicle.types'
import {
  PlateToVinResponseSchema,
  VehicleMakesSchema,
  VehicleModelsSchema,
  VehicleYearsSchema,
  VehicleDetailsResponseSchema
} from './vehicle.model'
import { VehicleUtils } from './vehicle.utils'
import { Observable, of, throwError } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { DrrHttpClient } from '../../http/drr-http-client'
import { RETRY_CONFIG } from '../../shared/services/http-context.tokens'

const EMPTY_PLATE_TO_VIN_RESPONSE: PlateToVinResponse = {
  vehicles: []
}

@Injectable({ providedIn: 'root' })
export class VehicleService {
  private http = inject(DrrHttpClient)
  private vehicleUtils = inject(VehicleUtils)

  defaultQueryParams = {
    showTypicalVehicles:true,
    showMotorcycles: false,
    showMotorhomes: false
  }

  getYears$(): Observable<VehicleYears> {
    return this.http.get<VehicleYears>('bff/vehicles/years').pipe(
      tap((response) => {
        VehicleYearsSchema.validateSync(response)
      })
    )
  }

  getMakes$(year: number, vehicleQueryParams?: VehicleRequestQueryParams): Observable<VehicleMakes> {
    const queryParams = vehicleQueryParams || this.defaultQueryParams

    return this.http.get<VehicleMakes>(`bff/vehicles/makes/${year}`, {
      params: this.stringifyQueryParams(queryParams)
    }).pipe(
      tap((response) => {
        VehicleMakesSchema.validateSync(response)
      })
    )
  }

  getModels$(year: number, make: string, vehicleQueryParams?: VehicleRequestQueryParams): Observable<VehicleModels> {
    const queryParams = vehicleQueryParams || this.defaultQueryParams

    if (make.includes('Motorhome/')) {
      make = this.vehicleUtils.normalizeMotorHome(make) as string
    }
    const encodedMake = this.vehicleUtils.uriEncode(make)

    return this.http.get<VehicleModels>(`bff/vehicles/models/${year}/${encodedMake}`, {
      params: this.stringifyQueryParams(queryParams)
    }).pipe(
      tap((response) => {
        VehicleModelsSchema.validateSync(response)
      })
    )
  }

  getVehicle$(tag: string, state: string): Observable<PlateToVinResponse> {
    const context = new HttpContext()
      .set(RETRY_CONFIG, { ...RETRY_CONFIG.defaultValue(), enabled: false })

    return this.http.get<PlateToVinResponse>('bff/vehicles/platetovin', {
      params: { tag, state },
      context
    }).pipe(
      tap((response) => {
        PlateToVinResponseSchema.validateSync(response)
      }),
      catchError((error) => {
        if (error?.response?.status === 404) {
          return of(EMPTY_PLATE_TO_VIN_RESPONSE)
        }
        return throwError(error)
      })
    )
  }

  getVehicleDetails$(year: number, make: string, model: string): Observable<VehicleDetailsResponse> {
    const [encodedMake, encodedModel] = this.vehicleUtils.uriEncode([make, model])

    return this.http.get<VehicleDetailsResponse>(
      `bff/vehicles/details/${year}/${encodedMake}/${encodedModel}`,
    ).pipe(
      tap(response => {
        VehicleDetailsResponseSchema.validateSync(response)
      })
    )
  }

  private stringifyQueryParams(queryParams: VehicleRequestQueryParams): Record<string, string> {
    return {
      showTypicalVehicles: String(queryParams.showTypicalVehicles),
      showMotorcycles: String(queryParams.showMotorcycles),
      showMotorhomes: String(queryParams.showMotorhomes)
    }
  }
}
